import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, History, Layers, Upload, X, Zap } from 'lucide-react'
import { useRole } from '@/app/role'
import { useCurrentSite } from '@/app/sites'
import { useSourceViewer } from '@/app/source-viewer'
import { PageHeader } from '@/app/shell/PageHeader'
import { useControls, useMappings, useQuestions, useReviewMapping, useSubmitResponse, useWithdrawn } from '@/lib/api/hooks'
import { computeCoverage, type QState } from '@/lib/coverage'
import { riskColor, tok } from '@/charts'
import { Button } from '@/ui/button'
import { Card, KLabel } from '@/ui/card'
import { RiskChip } from '@/ui/chip'
import { EvidenceCitation } from '@/ui/evidence-citation'
import { GradedResponse } from '@/ui/graded-response'
import { PackBadge } from '@/ui/pack-badge'
import { LoadingBlock, ErrorState } from '@/ui/states'
import { cn } from '@/lib/utils'

const RESP_TONE: Record<string, string> = {
  compliant: tok('--stable'), partially_effective: tok('--ember'), degraded: tok('--clear'),
  unverified: tok('--trace'), temporary_override: tok('--ember'), impaired: tok('--flare'),
  unknown: tok('--faint'), absent: tok('--ignition'),
}
const shortId = (qid: string) => qid.replace(/^BESS-/, '')

const ANS = tok('--stable'), REV = tok('--ember'), OPN = tok('--input')
const STATE_COL: Record<QState, string> = { answered: ANS, review: REV, open: OPN }
const STATE_LABEL: Record<QState, string> = { answered: 'answered', review: 'to review', open: 'open' }
type Filter = 'all' | QState

/** Segmented answered / to-review / open bar. */
function CovBar({ a, r, o, total, h = 22 }: { a: number; r: number; o: number; total: number; h?: number }) {
  const w = (n: number) => (total ? `${(n / total) * 100}%` : '0%')
  return (
    <div className="flex overflow-hidden rounded" style={{ height: h, border: h > 12 ? `1px solid ${tok('--border')}` : 'none' }}>
      <div style={{ width: w(a), background: ANS }} />
      <div style={{ width: w(r), background: REV }} />
      <div style={{ width: w(o), background: OPN }} />
    </div>
  )
}

export function AssessmentPage() {
  const { site, siteId } = useCurrentSite()
  const nav = useNavigate()
  const id = siteId ?? ''
  const { role } = useRole()
  const canWrite = role === 'assessor'
  const { open: openSource } = useSourceViewer()
  const questions = useQuestions()
  const controls = useControls(id)
  const mappings = useMappings(id)
  const submit = useSubmitResponse(id)
  const review = useReviewMapping(id)
  const withdrawn = useWithdrawn(id)

  const industry = site?.pack === 'DSEAR' ? 'petrochem' : 'BESS'
  const withdrawnByQ = new Map((withdrawn.data ?? []).map((w) => [w.question_id, w]))
  const cov = useMemo(
    () => computeCoverage(questions.data ?? [], controls.data ?? [], mappings.data ?? [], industry),
    [questions.data, controls.data, mappings.data, industry],
  )
  const allQ = useMemo(() => (questions.data ?? []).filter((q) => q.industry === industry), [questions.data, industry])
  const confByQ = new Map((controls.data ?? []).map((c) => [c.question_id, c.control_confidence]))

  const listRef = useRef<HTMLDivElement | null>(null)
  const [moduleId, setModuleId] = useState<string>('ALL')
  const [filter, setFilter] = useState<Filter>(cov.review > 0 ? 'review' : 'all')
  const [activeQ, setActiveQ] = useState<string | null>(null)
  const [picked, setPicked] = useState<Record<string, string>>({})
  const goReview = () => {
    setModuleId('ALL')
    setFilter('review')
    setActiveQ(null)
    setTimeout(() => listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
  }

  const listQ = allQ.filter((q) =>
    (moduleId === 'ALL' || q.module_id === moduleId) &&
    (filter === 'all' || cov.stateByQ.get(q.question_id) === filter))
  const active = activeQ && listQ.some((q) => q.question_id === activeQ) ? activeQ : listQ[0]?.question_id
  const activeObj = allQ.find((q) => q.question_id === active)
  const suggestion = active ? cov.mappingByQ.get(active) : undefined

  const header = (
    <PageHeader
      title="Assessment"
      subtitle={
        <>
          {site && <PackBadge pack={site.pack} />}
          <span>{cov.total} questions</span>
          <span className="text-faint">·</span>
          <span className="tabular">{cov.coveragePct}% coverage</span>
        </>
      }
      actions={
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => nav(`/site/${id}/evidence`)}>
            <Upload /> Add evidence
          </Button>
          {canWrite && cov.review > 0 && (
            <Button size="sm" onClick={goReview}>
              <Zap /> Review {cov.review} suggestions
            </Button>
          )}
        </div>
      }
    />
  )

  if (questions.isLoading || controls.isLoading)
    return (<>{header}<Card><LoadingBlock lines={6} /></Card></>)
  if (questions.isError) return (<>{header}<ErrorState onRetry={() => questions.refetch()} /></>)

  const save = () => {
    if (active && picked[active])
      submit.mutate({ question_id: active, response: picked[active], override_flag: false, impairment_flag: false, confidence_modifier: 0 })
  }

  return (
    <>
      {header}

      {site?.pack === 'DSEAR' && (
        <div className="mb-4 rounded-md border border-flare/40 bg-flare/[0.07] px-3 py-2 text-[12.5px] text-muted-foreground">
          Petrochem engine metadata is in progress — answers are recorded, but coverage confidence and the
          underwriting indices won&rsquo;t recompute yet.
        </div>
      )}

      {/* coverage summary */}
      <Card>
        <div className="mb-3.5 flex items-start justify-between gap-4">
          <div>
            <KLabel>Assessment status</KLabel>
            <div className="mt-1 font-display text-[40px] font-semibold leading-none tabular">
              {cov.coveragePct}%<span className="ml-1 text-[14px] font-medium text-muted-foreground">coverage</span>
            </div>
          </div>
          <div className="flex gap-6">
            {([['Answered', cov.answered, ANS], ['To review', cov.review, REV], ['Open', cov.open, OPN]] as const).map(([l, n, c]) => (
              <div key={l} className="text-right">
                <div className="font-display text-[24px] font-semibold tabular" style={{ color: c }}>{n}</div>
                <div className="text-[11px] text-faint">{l}</div>
              </div>
            ))}
          </div>
        </div>
        <CovBar a={cov.answered} r={cov.review} o={cov.open} total={cov.total} />
        <div className="mt-3 flex flex-wrap gap-4 text-[12px] text-muted-foreground">
          <span className="flex items-center gap-1.5"><i className="size-2.5 rounded-sm" style={{ background: ANS }} /> Answered — response recorded</span>
          <span className="flex items-center gap-1.5"><i className="size-2.5 rounded-sm" style={{ background: REV }} /> To review — AI suggestion awaiting accept/decline</span>
          <span className="flex items-center gap-1.5"><i className="size-2.5 rounded-sm" style={{ background: OPN }} /> Open — no answer, no suggestion</span>
        </div>
      </Card>

      {/* module breakdown */}
      <div className="mb-3 mt-6 flex items-center gap-2 font-display text-[13px] font-semibold">
        <Layers className="size-4" /> Coverage by module
        <span className="text-[11.5px] font-normal text-faint">— jump to where the gaps and suggestions are</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cov.modules.map((m) => (
          <button
            key={m.module_id}
            onClick={() => { setModuleId(m.module_id); setFilter('all'); setActiveQ(null) }}
            className={cn('rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary',
              moduleId === m.module_id && 'border-primary ring-2 ring-ring')}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="font-display text-[13px] font-semibold">{m.module_id}</div>
              <span className="tabular text-[13px] font-semibold" style={{ color: riskColor(m.conf) }}>{m.conf}</span>
            </div>
            <div className="mt-2.5"><CovBar a={m.answered} r={m.review} o={m.open} total={m.total} h={8} /></div>
            <div className="mt-2.5 flex justify-between text-[11.5px]">
              <span style={{ color: ANS }}>{m.answered} answered</span>
              <span style={{ color: m.review ? REV : tok('--faint'), fontWeight: m.review ? 600 : 400 }}>{m.review} to review</span>
              <span className="text-faint">{m.open} open</span>
            </div>
          </button>
        ))}
      </div>

      {/* question list + detail */}
      <div ref={listRef} className="mb-3 mt-6 flex scroll-mt-20 items-center gap-2 font-display text-[13px] font-semibold">
        {moduleId === 'ALL' ? 'All modules' : moduleId}
        <span className="text-[11.5px] font-normal text-faint">— {listQ.length} questions</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-[340px_1fr]">
        <Card className="overflow-hidden p-0">
          <div className="flex gap-1.5 border-b p-2.5">
            {(['all', 'review', 'open', 'answered'] as Filter[]).map((f) => (
              <button key={f} onClick={() => setFilter(f)}
                className={cn('rounded px-2 py-1 text-[11.5px] font-medium capitalize',
                  filter === f ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground')}
                style={filter === f && f === 'review' ? { color: tok('--ember') } : undefined}>
                {f === 'all' ? 'All' : f === 'review' ? 'To review' : f}
              </button>
            ))}
          </div>
          <div className="max-h-[520px] overflow-y-auto">
            {listQ.length === 0 ? (
              <div className="p-6 text-center text-[13px] text-muted-foreground">Nothing here — try another filter.</div>
            ) : listQ.map((q) => {
              const st = cov.stateByQ.get(q.question_id) ?? 'open'
              const on = q.question_id === active
              const conf = confByQ.get(q.question_id)
              return (
                <button key={q.question_id} onClick={() => setActiveQ(q.question_id)}
                  className={cn('flex w-full items-start gap-3 border-b px-4 py-3 text-left last:border-b-0 hover:bg-surface-2', on && 'bg-surface-2')}>
                  <span className="mt-1.5 size-2 shrink-0 rounded-full" style={{ background: STATE_COL[st] }} />
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 block text-[12.5px] font-medium">{q.question_text}</span>
                    <span className="mt-0.5 flex items-center gap-2">
                      <span className="tabular font-display text-[10.5px] text-faint">{shortId(q.question_id)}</span>
                      <span className="text-[10px] font-semibold" style={{ color: STATE_COL[st] }}>{STATE_LABEL[st]}</span>
                    </span>
                  </span>
                  {conf != null ? (
                    <span className="tabular mt-0.5 text-[12px] font-semibold" style={{ color: riskColor(conf) }}>{conf}</span>
                  ) : st === 'review' ? <Zap className="mt-0.5 size-3.5" style={{ color: REV }} /> : null}
                </button>
              )
            })}
          </div>
        </Card>

        <div>
          {activeObj ? (
            <div className="space-y-4">
              <Card>
                <KLabel>
                  {shortId(activeObj.question_id)} · {activeObj.module_name ?? activeObj.module_id}
                  {activeObj.barrier_id ? ` · Barrier ${activeObj.barrier_id}` : ''}
                </KLabel>
                <h3 className="mt-2 max-w-[62ch] text-[16px] font-semibold leading-snug">{activeObj.question_text}</h3>
                <div className="mb-2 mt-5 flex items-center justify-between">
                  <KLabel>Graded response</KLabel>
                  {confByQ.get(activeObj.question_id) != null && (
                    <span className="text-[11.5px] text-faint">current{' '}
                      <b className="tabular" style={{ color: riskColor(confByQ.get(activeObj.question_id) ?? 0) }}>{confByQ.get(activeObj.question_id)}</b>
                    </span>
                  )}
                </div>
                <GradedResponse value={picked[activeObj.question_id]} onChange={(v) => setPicked((p) => ({ ...p, [activeObj.question_id]: v }))} />
                {canWrite && (
                  <div className="mt-4 flex items-center gap-3">
                    <Button size="sm" onClick={save} disabled={!picked[activeObj.question_id] || submit.isPending}>
                      <Check /> {submit.isPending ? 'Saving…' : 'Save & recompute'}
                    </Button>
                    <span className="ml-auto text-[11.5px] text-faint">Answering recomputes the site engine.</span>
                  </div>
                )}
              </Card>

              {active && !confByQ.has(active) && withdrawnByQ.get(active) && (
                <Card className="border-flare/40 bg-flare/[0.05]">
                  <div className="flex items-start gap-2.5">
                    <History className="mt-0.5 size-4 shrink-0 text-flare" />
                    <div className="text-[12.5px]">
                      <div className="font-semibold">Evidence withdrawn — reverted to open</div>
                      <div className="mt-0.5 text-muted-foreground">
                        Previously answered{' '}
                        {withdrawnByQ.get(active)!.suggested_response && <b>{withdrawnByQ.get(active)!.suggested_response}</b>} from{' '}
                        <b>{withdrawnByQ.get(active)!.file_name}</b>
                        {withdrawnByQ.get(active)!.deleted_by ? ` · withdrawn by ${withdrawnByQ.get(active)!.deleted_by}` : ''}
                        {withdrawnByQ.get(active)!.deleted_at ? ` · ${new Date(withdrawnByQ.get(active)!.deleted_at!).toLocaleDateString()}` : ''}.
                        {' '}Re-answer, or re-upload the document to restore.
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {suggestion ? (
                <Card className="border-primary/40">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-1.5 font-display text-[10px] font-semibold uppercase tracking-wider text-primary">
                      <Zap className="size-3.5" /> AI-suggested answer
                    </div>
                    {suggestion.suggested_response && <RiskChip label={suggestion.suggested_response} tone={RESP_TONE[suggestion.suggested_response] ?? tok('--muted-foreground')} />}
                  </div>
                  <EvidenceCitation
                    quote={suggestion.evidence_quote ?? '(no quote)'}
                    source={`match confidence ${Math.round(Number(suggestion.confidence ?? 0) * 100)}%`}
                    verified={suggestion.quote_verified ?? false}
                  />
                  {canWrite && (
                    <div className="mt-3 flex items-center gap-2">
                      <Button size="sm" onClick={() => review.mutate({ mappingId: suggestion.mapping_id, action: 'confirm' })}
                        disabled={review.isPending}>
                        <Check /> Accept &amp; auto-answer
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => review.mutate({ mappingId: suggestion.mapping_id, action: 'reject' })}
                        disabled={review.isPending}>
                        <X /> Decline
                      </Button>
                      {suggestion.file_id && (
                        <button onClick={() => openSource(suggestion.file_id!, suggestion.mapping_id)} className="ml-auto text-[12px] text-muted-foreground hover:text-foreground">
                          Open source →
                        </button>
                      )}
                    </div>
                  )}
                </Card>
              ) : (
                <Card className="border-dashed">
                  <div className="flex items-center gap-1.5 font-display text-[10px] font-semibold uppercase tracking-wider text-faint">
                    <Zap className="size-3.5 text-primary" /> AI-suggested answer
                  </div>
                  <p className="mt-1.5 text-[12.5px] text-muted-foreground">
                    Suggestions with a verified evidence quote appear here once documents are processed and mapped on the Evidence tab.
                  </p>
                </Card>
              )}
            </div>
          ) : (
            <Card><p className="text-sm text-muted-foreground">Select a question.</p></Card>
          )}
        </div>
      </div>
    </>
  )
}
